'use client';

import React, { useState, useMemo } from 'react';
import { ClipboardIcon, CheckCircleIcon, WaterDropIcon } from '../../Icons';
import { PinterestSaveButton } from '../../PinterestSaveButton';
import { getCanonicalUrl } from '../../utils/seo';
import { useUnits } from '../../../contexts/UnitContext';
import { ProfessionalPrintLayout } from '../../ProfessionalPrintLayout';

const GANACHE_RATIOS: Record<string, Record<string, number>> = {
    drip: { white: 3, ruby: 3, milk: 2.5, semisweet: 1, bittersweet: 1 },
    filling: { white: 3.5, ruby: 3.5, milk: 3, semisweet: 1.5, bittersweet: 1.5 },
    firm: { white: 4, ruby: 4, milk: 3.5, semisweet: 2, bittersweet: 2 },
    truffles: { white: 4.5, ruby: 4.5, milk: 4, semisweet: 2.5, bittersweet: 2.5 }
};

const chocolateTypes = [
    { value: 'white', label: 'White Chocolate' },
    { value: 'ruby', label: 'Ruby Chocolate' },
    { value: 'milk', label: 'Milk Chocolate' },
    { value: 'semisweet', label: 'Semi-Sweet Chocolate' },
    { value: 'bittersweet', label: 'Bittersweet (60-70%)' },
];

const ganacheTypes = [
    { value: 'drip', label: 'Glaze / Drip' },
    { value: 'filling', label: 'Soft Filling' },
    { value: 'firm', label: 'Firm Piping' },
    { value: 'truffles', label: 'Truffles' },
];

export const GanacheCalculatorClient: React.FC = () => {
    const [chocolateAmount, setChocolateAmount] = useState('200');
    const [chocolateType, setChocolateType] = useState('semisweet');
    const [ganacheType, setGanacheType] = useState('drip');
    const [projectName, setProjectName] = useState('');
    const [copied, setCopied] = useState(false);
    const { activeUnit, convertValue, formatValue } = useUnits();

    const { creamAmount, ratio } = useMemo(() => {
        const amount = parseFloat(chocolateAmount) || 0;
        if (amount === 0) return { creamAmount: 0, ratio: 'N/A' };
        const ratioValue = GANACHE_RATIOS[ganacheType][chocolateType];
        return { creamAmount: parseFloat((amount / ratioValue).toFixed(1)), ratio: `${ratioValue}:1` };
    }, [chocolateAmount, chocolateType, ganacheType]);

    const handleCopy = async () => {
        if (creamAmount > 0) {
            try {
                await navigator.clipboard.writeText(`${formatValue(convertValue(creamAmount, 'g', activeUnit))} ${activeUnit}`);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {}
        }
    };

    return (
        <div className="grid md:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl border border-brand-border shadow-sm">
                    <label className="block text-xs font-bold text-brand-text-body/60 mb-2 uppercase tracking-wide">Project Name</label>
                    <input type="text" placeholder="e.g. Chocolate Drip Cake" value={projectName} onChange={(e) => setProjectName(e.target.value)} className="w-full px-3 py-2 bg-white border border-brand-border rounded-lg text-sm text-brand-text-body" />
                </div>
                <div>
                    <label className="block text-sm font-bold text-brand-text-title mb-2">1. Chocolate Type</label>
                    <select value={chocolateType} onChange={e => setChocolateType(e.target.value)} className="w-full px-3 py-2 bg-white border border-brand-border rounded-lg text-brand-text-body">
                        {chocolateTypes.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-bold text-brand-text-title mb-2">2. Consistency</label>
                    <select value={ganacheType} onChange={e => setGanacheType(e.target.value)} className="w-full px-3 py-2 bg-white border border-brand-border rounded-lg text-brand-text-body">
                        {ganacheTypes.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-bold text-brand-text-title mb-2">3. Chocolate Weight</label>
                    <div className="relative">
                        <input type="number" value={chocolateAmount} onChange={e => setChocolateAmount(e.target.value)} className="w-full px-3 py-2 bg-white border border-brand-border rounded-lg text-brand-text-body" />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold italic">g</span>
                    </div>
                </div>
            </div>
            <div className="bg-brand-pink-light p-6 rounded-lg text-center sticky top-6">
                <WaterDropIcon className="w-12 h-12 mx-auto text-brand-pink mb-2" />
                <h3 className="font-serif font-bold text-2xl text-brand-text-title mb-2">Cream Needed</h3>
                <div className="bg-white p-4 rounded-lg border border-brand-pink/20 flex flex-col items-center">
                    <div className="flex items-center justify-center gap-2">
                        <p className="font-serif font-bold text-5xl text-brand-pink">{formatValue(convertValue(creamAmount, 'g', activeUnit))}</p>
                        <span className="text-xl font-semibold text-brand-text-body pt-4">{activeUnit}</span>
                        <button onClick={handleCopy} className="p-1 self-start mt-2">
                            {copied ? <CheckCircleIcon className="w-6 h-6 text-green-500" /> : <ClipboardIcon className="w-6 h-6 text-brand-text-body/50 hover:text-brand-pink" />}
                        </button>
                    </div>
                    <p className="text-xs text-brand-text-body/80 mt-2 mb-4 text-brand-text-body">Ratio: <strong>{ratio}</strong></p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full">
                        <PinterestSaveButton 
                            url={getCanonicalUrl('/tools/ganache-calculator')} 
                            media={getCanonicalUrl('/calculators/ganache.jpg')} 
                            description={`Ratio ${ratio} Ganache!`} 
                            className="w-full justify-center" 
                        />
                        <button onClick={() => window.print()} className="py-4 bg-brand-text-title hover:bg-black text-white rounded-xl font-bold transition-all shadow-md flex items-center justify-center">PDF</button>
                    </div>
                </div>
            </div>
            <ProfessionalPrintLayout 
                title="Ganache Recipe Sheet" subtitle={projectName || "Custom Ganache Batch"}
                items={[
                    { name: "Chocolate Type", value: chocolateTypes.find(t => t.value === chocolateType)?.label || chocolateType },
                    { name: "Ganache Target", value: ganacheTypes.find(t => t.value === ganacheType)?.label || ganacheType },
                    { name: "Chocolate Weight", value: `${chocolateAmount} g` },
                    { name: "Heavy Cream Needed", value: `${formatValue(convertValue(creamAmount, 'g', activeUnit))} ${activeUnit}` },
                    { name: "Mixing Ratio", value: ratio }
                ]}
                total={{ label: "Total Volume", value: `${formatValue(convertValue(parseFloat(chocolateAmount) + creamAmount, 'g', activeUnit))} ${activeUnit}` }}
            />
        </div>
    );
};
